import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import LinkIcon from "@mui/icons-material/Link";
import SearchIcon from "@mui/icons-material/Search";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FileTypeIcon from "./FileTypeIcon";
import {
  formatBytes,
  formatDateTime,
  formatRelativeTime,
  getFileExtension,
  getFileCategory,
  buildFileUrl,
  copyFileLink,
  FILES_HEADER_HEIGHT,
} from "./filesUtils";

const FILES_PER_PAGE = 25;

const CATEGORY_COLORS = {
  image: { bg: "#f3e8ff", color: "#7c3aed" },
  document: { bg: "#dbeafe", color: "#1d4ed8" },
  code: { bg: "#d1fae5", color: "#047857" },
  video: { bg: "#fee2e2", color: "#b91c1c" },
  audio: { bg: "#fef3c7", color: "#b45309" },
  archive: { bg: "#f1f5f9", color: "#475569" },
  text: { bg: "#cffafe", color: "#0e7490" },
  other: { bg: "#f1f5f9", color: "#64748b" },
};

export default function FilesTable({
  files,
  filesLoading,
  totalFileSize,
  headerTitle,
  emptyMessage,
  selectable,
  selectedFiles,
  onSelectFile,
  onSelectAll,
  previewFile,
  onClickFile,
  headerActions,
  onRefresh,
  onSnackbar,
}) {
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  // Reset page when files change
  useEffect(() => {
    setCurrentPage(0);
    setSearchValue("");
  }, [files]);

  const filteredFiles = useMemo(() => {
    if (!searchValue.trim()) return files;
    const searchLower = searchValue.toLowerCase();
    return files.filter((file) =>
      (file.fileName || "").toLowerCase().includes(searchLower)
    );
  }, [files, searchValue]);

  const totalPages = Math.ceil(filteredFiles.length / FILES_PER_PAGE);
  const paginatedFiles = useMemo(() => {
    const start = currentPage * FILES_PER_PAGE;
    return filteredFiles.slice(start, start + FILES_PER_PAGE);
  }, [filteredFiles, currentPage]);

  // Clamp page if out of range
  useEffect(() => {
    if (currentPage > 0 && currentPage > totalPages - 1) {
      setCurrentPage(Math.max(totalPages - 1, 0));
    }
  }, [currentPage, totalPages]);

  const paginatedIds = paginatedFiles.map((f) => f.id);
  const allOnPageSelected =
    selectable &&
    paginatedIds.length > 0 &&
    paginatedIds.every((id) => selectedFiles?.includes(id));
  const someOnPageSelected =
    selectable && paginatedIds.some((id) => selectedFiles?.includes(id));

  const handleCopyLink = async (fileId) => {
    const ok = await copyFileLink(fileId);
    if (onSnackbar) {
      onSnackbar(
        ok ? "File link copied to clipboard" : "Failed to copy file link",
        ok ? "success" : "error"
      );
    }
  };

  const selectedSize = useMemo(() => {
    if (!selectable || !selectedFiles?.length) return 0;
    return selectedFiles.reduce((sum, id) => {
      const f = files.find((file) => file.id === id);
      return sum + (f?.fileSize || 0);
    }, 0);
  }, [selectable, selectedFiles, files]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* Header — height must match FilesSidebar header for aligned border */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          height: FILES_HEADER_HEIGHT,
          minHeight: FILES_HEADER_HEIGHT,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          gap: 2,
          flexShrink: 0,
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem" }}>
            {headerTitle}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8125rem" }}>
            {selectable && selectedFiles?.length > 0 ? (
              <>
                {selectedFiles.length} selected &middot; {formatBytes(selectedSize)}
              </>
            ) : (
              <>
                {filteredFiles.length} file{filteredFiles.length !== 1 ? "s" : ""} &middot;{" "}
                {formatBytes(totalFileSize)}
              </>
            )}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {headerActions}
          <TextField
            size="small"
            placeholder="Search files..."
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              setCurrentPage(0);
            }}
            sx={{
              width: 200,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.default",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {/* Table */}
      <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {filesLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
            }}
          >
            <CircularProgress size={32} />
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 2 }}>
              Loading files...
            </Typography>
          </Box>
        ) : filteredFiles.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              textAlign: "center",
            }}
          >
            <FolderOpenIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              {searchValue
                ? "No files match your search"
                : emptyMessage || "No files found"}
            </Typography>
          </Box>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {selectable && (
                  <TableCell sx={{ width: 44, p: 1, bgcolor: "background.default" }}>
                    <Checkbox
                      size="small"
                      checked={allOnPageSelected}
                      indeterminate={someOnPageSelected && !allOnPageSelected}
                      onChange={onSelectAll}
                      sx={{ p: 0 }}
                    />
                  </TableCell>
                )}
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    bgcolor: "background.default",
                    py: 1,
                  }}
                >
                  File Name
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    bgcolor: "background.default",
                    py: 1,
                    width: 90,
                  }}
                >
                  Type
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    bgcolor: "background.default",
                    py: 1,
                    width: 100,
                  }}
                >
                  Size
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    bgcolor: "background.default",
                    py: 1,
                    width: 140,
                  }}
                >
                  Date Created
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    bgcolor: "background.default",
                    py: 1,
                    width: 100,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedFiles.map((file) => {
                const isSelected = selectable && selectedFiles?.includes(file.id);
                const isPreviewing = previewFile?.id === file.id;
                const ext = getFileExtension(file.fileName);
                const category = getFileCategory(file.fileName);
                const chipColors = CATEGORY_COLORS[category] || CATEGORY_COLORS.other;

                return (
                  <TableRow
                    key={file.id}
                    hover
                    selected={isSelected}
                    onClick={
                      onClickFile
                        ? (e) => {
                            // Don't trigger preview when clicking checkbox or action buttons
                            if (e.target.closest("[data-no-preview]")) return;
                            onClickFile(file);
                          }
                        : undefined
                    }
                    sx={{
                      cursor: onClickFile ? "pointer" : "default",
                      ...(isPreviewing && {
                        borderLeft: "3px solid",
                        borderLeftColor: "primary.main",
                        bgcolor: "action.selected",
                      }),
                      "&:last-child td": { borderBottom: 0 },
                      "& td": { py: 0.75 },
                    }}
                  >
                    {selectable && (
                      <TableCell sx={{ width: 44, p: 1 }} data-no-preview="true">
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={() => onSelectFile(file.id)}
                          sx={{ p: 0 }}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                        <FileTypeIcon fileName={file.fileName} />
                        <Tooltip title={file.fileName || "Unnamed file"}>
                          <Typography
                            noWrap
                            sx={{
                              fontSize: "0.8125rem",
                              maxWidth: 280,
                            }}
                          >
                            {file.fileName || "Unnamed file"}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {ext ? (
                        <Chip
                          label={ext}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "0.6875rem",
                            fontWeight: 600,
                            bgcolor: chipColors.bg,
                            color: chipColors.color,
                            borderRadius: 1,
                            textTransform: "uppercase",
                          }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ color: "text.disabled" }}>
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8125rem" }}>
                        {formatBytes(file.fileSize)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={formatDateTime(file.createDate)}>
                        <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
                          {formatRelativeTime(file.createDate)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell data-no-preview="true">
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Copy link">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyLink(file.id);
                            }}
                            sx={{ color: "text.secondary" }}
                          >
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            component="a"
                            href={buildFileUrl(file.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            sx={{ color: "text.secondary" }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            py: 1.5,
            px: 2,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.default",
          }}
        >
          <Button
            size="small"
            disabled={currentPage === 0}
            onClick={() => setCurrentPage((p) => p - 1)}
            startIcon={<ChevronLeftIcon />}
          >
            Previous
          </Button>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Page {currentPage + 1} of {totalPages}
          </Typography>
          <Button
            size="small"
            disabled={currentPage >= totalPages - 1}
            onClick={() => setCurrentPage((p) => p + 1)}
            endIcon={<ChevronRightIcon />}
          >
            Next
          </Button>
        </Box>
      )}
    </Box>
  );
}
